import { v4 as uuidv4 } from 'uuid';

export const extractFromResponse = (response, level1, level2) => {
  return response?.features?.[0]?.properties?.context?.[level1]?.[level2] || null;
};

/**
 * Returns an array of wishlist items associated with a user's profile.
 * @param {object} currentUser - The user object.
 * @returns {array} An array of wishlist items, or an empty array if input is invalid or wishlist is missing.
 */
export const getWishlist = currentUser => {
  return (
    (currentUser && currentUser.id && currentUser?.attributes?.profile?.publicData?.wishlist) || []
  );
};
/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const upsertWishlistItem = (currentUser, params) => {
  // Retrieve the current user's wishlist, which is expected to be an array of objects
  const wishlist = getWishlist(currentUser);

  // Destructure the params to get wishlistItem and wishlistName
  const { wishlistItem, wishlistName, listings = [] } = params;

  // Check if there's a wishlist with the given wishlistName
  const wishlistIndex = Array.isArray(wishlist)
    ? wishlist.findIndex(item => item.name === wishlistName)
    : -1;

  const selectedListing =
    isArrayLength(listings) && listings.find(l => l.id?.uuid === wishlistItem);

  const images = selectedListing ? selectedListing.images : [];
  const firstImage = isArrayLength(images)
    ? images[0]?.attributes?.variants['listing-card']?.url
    : null;

  // If the wishlist name is found in the array
  if (wishlistIndex !== -1) {
    // Check if the wishlist item ID is already in the ids array; if not, add it
    if (!wishlist[wishlistIndex].ids.includes(wishlistItem)) {
      wishlist[wishlistIndex].ids.push(wishlistItem);
    }
  } else {
    // If the wishlist name is not found, create a new entry in the wishlist
    wishlist.push({
      id: uuidv4(),
      name: wishlistName,
      ids: [wishlistItem],
      coverImage: firstImage,
    });
  }

  return wishlist;
};

export const getAverageValidRatings = reviews => {
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, review) => sum + review.attributes.rating, 0) / reviews.length
      : 0.0;

  return averageRating;
};
export const getStoreName = author => {
  return author && author.id && author?.attributes?.profile?.publicData?.storeName;
};
/**
 * Returns the display name of an author.
 * @param {object} author - The author object.
 * @returns {string} The display name, or an empty string if input is invalid.
 */
export const getDisplayName = author => {
  const profile = author && author.id && author?.attributes?.profile;
  const isNameAccessible = profile?.firstName;
  const name = isNameAccessible ? profile?.firstName + ' ' + profile?.lastName : 'Sb Market User';
  return isNameAccessible ? name : profile?.displayName;
};

export const getStoreNameOrDisplayName = author => {
  const storeName = getStoreName(author);
  const displayName = getDisplayName(author);
  return storeName || displayName;
};

export const getMerchantId = currentUser => {
  if (!currentUser || !currentUser?.id) return null;
  return currentUser?.attributes?.profile?.protectedData?.merchantId;
};

export const isPaypalConnected = currentUser => {
  if (!currentUser || !currentUser?.id) return null;
  return currentUser?.attributes?.profile?.publicData?.isPaypalConnected;
};

export const isStripeConnected = currentUser => {
  if (!currentUser || !currentUser?.id) return null;
  return currentUser?.attributes?.profile?.publicData?.isStripeConnected;
};

export const isString = input => {
  return typeof input === 'string';
};

